import { Link } from "react-router-dom";
import {
    HandHeartIcon,
    ShieldSlashIcon,
    ProhibitIcon,
    UsersThreeIcon,
    WarningIcon,
    SealCheckIcon,
} from "@phosphor-icons/react";
import Seo from "@/components/Seo";

const PILLARS = [
    {
        icon: HandHeartIcon,
        title: "Respect every Indian",
        body: "Hate speech, harassment, discrimination based on caste, religion, region, gender, sexual orientation or disability has no place here. Different opinions are welcome — personal attacks are not.",
        color: "#FFA700",
    },
    {
        icon: ShieldSlashIcon,
        title: "No harmful content",
        body: "We strictly prohibit content promoting self-harm, suicide, drug abuse, terrorism, or violence against people or animals. We work closely with NIMHANS and crisis-helpline partners for sensitive topics.",
        color: "#E01A4F",
    },
    {
        icon: ProhibitIcon,
        title: "No sexual or graphic content",
        body: "India Reels is safe for all ages. Nudity, sexually explicit material, and gratuitously violent or gory content are not allowed. Educational/medical exceptions reviewed case-by-case.",
        color: "#FFA700",
    },
    {
        icon: UsersThreeIcon,
        title: "Protect minors",
        body: "Content sexualising minors is illegal and will be reported to Indian authorities (NCRB, CBI cyber cell). Creators under 18 must use family-safe handles and have parental guidance.",
        color: "#E01A4F",
    },
    {
        icon: WarningIcon,
        title: "No misinformation",
        body: "Don't post fake news, election misinformation, doctored political content, anti-vaccine claims, or financial scams. Misleading deepfakes will be removed and the account suspended.",
        color: "#FFA700",
    },
    {
        icon: SealCheckIcon,
        title: "Authentic engagement",
        body: "No bots, no bought followers, no fake likes. We use AI + manual review to detect inauthentic activity and shadow-ban or remove repeat offenders.",
        color: "#E01A4F",
    },
];

const ENFORCEMENT = [
    { step: "1. Warning", desc: "First minor violation: in-app warning. Content removed; account stays active." },
    { step: "2. Temporary suspension", desc: "Second violation or one major violation: 7–30 day suspension." },
    { step: "3. Permanent ban", desc: "Third violation, repeat major violations, or illegal content: permanent account termination + report to authorities if applicable." },
    { step: "4. Appeals", desc: "Disagree with a decision? Email appeals@india-reels.com within 14 days. Independent reviewer reads every appeal." },
];

export default function Community() {
    return (
        <div className="px-5 md:px-10 max-w-5xl mx-auto py-16 md:py-24" data-testid="community-page">
            <Seo title="Community Guidelines — India Reels" description="India Reels Community Guidelines — the rules that keep our platform safe, respectful and fun for every Indian." path="/community-guidelines" />
            <div className="text-xs uppercase tracking-[0.3em] text-secondary font-bold mb-3">
                community guidelines
            </div>
            <h1 className="font-heading font-black text-4xl md:text-6xl leading-tight">
                A safe stage for <span className="gradient-text">every Indian</span>.
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-3xl leading-relaxed">
                India Reels is a community of 50,000+ creators and millions of viewers. These
                guidelines exist so the platform stays a place where people feel safe to express,
                create and grow.
            </p>

            <div className="mt-12 grid md:grid-cols-2 gap-4">
                {PILLARS.map((p) => (
                    <div
                        key={p.title}
                        data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                        className="rounded-3xl border border-white/10 bg-surface p-7 hover:border-white/20 transition-colors"
                    >
                        <div
                            className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
                            style={{ background: `${p.color}1F` }}
                        >
                            <p.icon size={26} weight="fill" color={p.color} />
                        </div>
                        <h3 className="font-heading font-bold text-xl mb-2">{p.title}</h3>
                        <p className="text-text-secondary text-sm leading-relaxed">{p.body}</p>
                    </div>
                ))}
            </div>

            <div className="mt-20">
                <div className="text-xs uppercase tracking-[0.3em] text-secondary font-bold mb-3">
                    enforcement
                </div>
                <h2 className="font-heading font-black text-3xl md:text-4xl mb-8">
                    How we enforce these rules
                </h2>
                <div className="space-y-3">
                    {ENFORCEMENT.map((e, i) => (
                        <div
                            key={i}
                            data-testid={`enforcement-${i}`}
                            className="flex flex-col sm:flex-row gap-3 sm:gap-6 rounded-3xl border border-white/10 bg-surface/60 p-6"
                        >
                            <div className="sm:w-48 shrink-0 font-heading font-bold text-secondary">
                                {e.step}
                            </div>
                            <div className="text-white/80 text-sm leading-relaxed">{e.desc}</div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-16 rounded-3xl bg-gradient-to-br from-primary/20 via-surface to-secondary/10 border border-white/10 p-10 md:p-14 text-center">
                <h3 className="font-heading font-black text-2xl md:text-3xl">
                    Saw something that violates these rules?
                </h3>
                <p className="text-text-secondary mt-3 max-w-xl mx-auto">
                    Report directly inside the app or email{" "}
                    <a className="text-primary" href="mailto:moderation@india-reels.com">
                        moderation@india-reels.com
                    </a>
                    . We review every report within 24 hours.
                </p>
                <Link
                    to="/support"
                    data-testid="community-report-cta"
                    className="btn-primary mt-7 px-6 py-3 inline-flex"
                >
                    Report via Support
                </Link>
            </div>
        </div>
    );
}
